import React from "react";

/**
 * Inline SVG platform logos for the Algorithm page.
 * Real brand marks (simplified for scale), not colored placeholder squares.
 * All logos rendered at 40x40 by default; scale via `size` prop.
 */

const wrap = (size, bg, children) => (
  <div
    className="rounded-xl flex items-center justify-center shadow-sm"
    style={{ width: size, height: size, background: bg }}
  >
    {children}
  </div>
);

export function YouTubeLogo({ size = 40 }) {
  return wrap(size, "#FF0000",
    <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 24 24" fill="white">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

export function YouTubeShortsLogo({ size = 40 }) {
  return (
    <div
      className="rounded-xl flex items-center justify-center shadow-sm relative overflow-hidden"
      style={{ width: size, height: size, background: "linear-gradient(135deg, #FF0033 0%, #FF3355 100%)" }}
    >
      <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 24 24" fill="white">
        <path d="M17.77 10.32l-1.2-.5L18 9.06c1.84-1 2.53-3.37 1.53-5.22-.7-1.29-2.03-2.08-3.5-2.08-.68 0-1.34.17-1.92.5L4 7.63C2.16 8.63 1.47 11 2.47 12.85c.5.91 1.34 1.57 2.32 1.82l1.2.5L4 16.94c-1.84 1-2.53 3.37-1.53 5.22.7 1.29 2.04 2.08 3.5 2.08.68 0 1.34-.17 1.92-.5L18 17.62c1.84-1 2.53-3.36 1.53-5.21-.5-.91-1.34-1.57-2.32-1.82zm-7.16 4.15v-4.94l4.65 2.47-4.65 2.47z"/>
      </svg>
    </div>
  );
}

export function InstagramReelsLogo({ size = 40 }) {
  const id = React.useId();
  return (
    <div className="rounded-xl flex items-center justify-center shadow-sm" style={{ width: size, height: size,
      background: "linear-gradient(45deg, #F58529 0%, #DD2A7B 40%, #8134AF 70%, #515BD4 100%)" }}>
      <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8">
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <path d="M3 8h18" />
        <path d="M8 3l2.5 5H6z" fill="white" stroke="none" />
        <path d="M14 3l2.5 5H12z" fill="white" stroke="none" />
        <path d="M10 12l6 3.5-6 3.5z" fill="white" stroke="none" />
      </svg>
    </div>
  );
}

export function TikTokLogo({ size = 40 }) {
  return wrap(size, "#000",
    <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 24 24">
      <path fill="#25F4EE" d="M13.5 3v11.2a3.3 3.3 0 1 1-3.3-3.3v3.5a1 1 0 1 0 1 1V3z" transform="translate(-1 0)"/>
      <path fill="#FE2C55" d="M14 3v11.2a3.3 3.3 0 1 1-3.3-3.3v3.5a1 1 0 1 0 1 1V3z" transform="translate(1 0)"/>
      <path fill="white" d="M13.5 3v11.2a3.3 3.3 0 1 1-3.3-3.3v3.5a1 1 0 1 0 1 1V3zm3.5 0h-3.5c.2 2.4 2.1 4.3 4.5 4.5V3.9c-.6-.2-1-.5-1-.9z"/>
    </svg>
  );
}

export function FacebookLogo({ size = 40 }) {
  return wrap(size, "#1877F2",
    <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 24 24" fill="white">
      <path d="M13.5 21v-8h2.7l.4-3.1h-3.1v-2c0-.9.3-1.5 1.5-1.5H16.6V3.6c-.3 0-1.4-.1-2.7-.1-2.6 0-4.4 1.6-4.4 4.5v2.5H7v3.1h2.5V21h4z"/>
    </svg>
  );
}

export function XLogo({ size = 40 }) {
  return wrap(size, "#000",
    <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 24 24" fill="white">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.816L4.99 21.75H1.68l7.73-8.836L1.254 2.25h6.828l4.713 6.231z"/>
    </svg>
  );
}

export function LinkedInLogo({ size = 40 }) {
  return wrap(size, "#0A66C2",
    <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 24 24" fill="white">
      <path d="M6.5 8h-4v13h4V8zM4.5 2.5A2.5 2.5 0 1 0 4.5 7.5a2.5 2.5 0 0 0 0-5zM21.5 21v-7c0-3.7-2-5.4-4.6-5.4-2.1 0-3.1 1.2-3.6 2v-1.7h-4V21h4v-7.3c0-.4.03-.9.16-1.2.34-.9 1.16-1.7 2.5-1.7 1.77 0 2.5 1.35 2.5 3.3V21h3.04z"/>
    </svg>
  );
}

export function PinterestLogo({ size = 40 }) {
  return wrap(size, "#E60023",
    <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 24 24" fill="white">
      <path d="M12 0a12 12 0 0 0-4.4 23.16c-.1-.9-.2-2.3.04-3.3.22-.9 1.4-5.7 1.4-5.7s-.35-.7-.35-1.75c0-1.64.95-2.87 2.13-2.87 1 0 1.5.75 1.5 1.65 0 1-.65 2.5-.98 3.9-.28 1.16.6 2.1 1.73 2.1 2.08 0 3.68-2.2 3.68-5.36 0-2.8-2.01-4.76-4.9-4.76-3.33 0-5.29 2.5-5.29 5.08 0 1 .38 2.08.87 2.66.1.11.11.22.08.34l-.32 1.32c-.05.21-.17.26-.4.16-1.47-.68-2.4-2.83-2.4-4.55 0-3.7 2.7-7.11 7.76-7.11 4.08 0 7.24 2.9 7.24 6.79 0 4.05-2.55 7.3-6.09 7.3-1.19 0-2.31-.62-2.69-1.35l-.73 2.78c-.26 1.02-.98 2.29-1.46 3.07A12 12 0 1 0 12 0z"/>
    </svg>
  );
}

export function SnapchatLogo({ size = 40 }) {
  return wrap(size, "#FFFC00",
    <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 24 24" fill="black">
      <path d="M12 2c3.4 0 5.6 2.4 5.6 5.4 0 1 0 2.6-.1 3.4.4.2 1 .5 1.2.5.3 0 .8-.3 1.3-.3.4 0 .8.3.8.7 0 .8-2.3 1.1-2.5 1.7-.05.2 1 1.3 1.8 2.1.3.3.6.5.6.8 0 .8-2.2 1.1-2.5 1.4-.3.2-.2 1.4-.7 1.7-.4.2-1.7-.2-2.7 0-1 .2-2 1.7-3.5 1.7-1.5 0-2.3-1.5-3.5-1.7-1-.2-2.3.3-2.7 0-.5-.3-.4-1.5-.7-1.7-.3-.2-2.5-.6-2.5-1.4 0-.3.3-.5.6-.8.8-.8 1.85-1.9 1.8-2.1-.2-.6-2.5-.9-2.5-1.7 0-.4.4-.7.8-.7.5 0 1 .3 1.3.3.2 0 .8-.3 1.2-.5-.1-.8-.1-2.4-.1-3.4C6.4 4.4 8.6 2 12 2z"/>
    </svg>
  );
}

export function ThreadsLogo({ size = 40 }) {
  return wrap(size, "#000",
    <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 24 24" fill="white">
      <path d="M17.6 10.9c-.1 0-.2-.1-.3-.1-.2-3-1.9-4.7-4.7-4.7-1.7 0-3.1.7-4 2.1l1.6 1.1c.6-1 1.5-1.2 2.3-1.2 1 0 1.7.3 2.2.9.3.4.5.9.6 1.6-.7-.1-1.4-.2-2.3-.2-2.5 0-4.2 1.3-4.2 3.3 0 1.9 1.6 3 3.3 3 2.2 0 3.5-1.4 3.9-3.4.9.5 1.7 1.4 1.7 2.7 0 1.7-1.5 3.2-4.2 3.2-1.8 0-3.4-.7-4.3-2.5-.9-1.6-1.1-3.5-1.1-5 0-1.5.2-3.4 1.1-5C10.3 4.9 11.9 4.2 13.7 4.2c2.3 0 4 1 4.9 2.4.6.9 1 2.1 1.1 3.6.1.2-.4.6-2.1.7zm-4.9 4.3c-.9 0-1.5-.5-1.5-1.2 0-.9 1-1.4 2.6-1.4.6 0 1.2.1 1.6.1-.2 1.7-1.1 2.5-2.7 2.5z"/>
    </svg>
  );
}

export function SpotifyLogo({ size = 40 }) {
  return wrap(size, "#1DB954",
    <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 24 24" fill="white">
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.6 14.4c-.2.3-.5.4-.8.2-2.2-1.3-4.9-1.6-8.1-.9-.3.1-.6-.1-.7-.4-.1-.3.1-.6.4-.7 3.5-.8 6.5-.4 8.9 1.1.3.1.4.5.3.7zm1.2-2.7c-.2.4-.6.5-1 .3-2.5-1.5-6.3-2-9.2-1.1-.4.1-.8-.1-.9-.5-.1-.4.1-.8.5-.9 3.3-1 7.5-.4 10.4 1.3.4.2.5.5.2.9zm.1-2.8C15 9.2 9.9 9 7 9.9c-.4.1-.8-.1-.9-.5-.1-.4.1-.8.5-.9 3.3-1 9-.8 12.2 1.1.4.2.5.7.3 1.1-.2.4-.7.5-1 .3z"/>
    </svg>
  );
}

/** Master map used by the Algorithm page. Keys MUST match the backend's
    `/api/algorhytm/platforms` response keys — mismatches silently render
    a platform as "Soon" instead of active. */
export const PLATFORM_LOGOS = {
  youtube:    { name: "YouTube",          color: "#FF0000", Logo: YouTubeLogo },
  shorts:     { name: "YouTube Shorts",   color: "#FF0033", Logo: YouTubeShortsLogo },
  reels:      { name: "Instagram Reels",  color: "#DD2A7B", Logo: InstagramReelsLogo },
  tiktok:     { name: "TikTok",           color: "#000",    Logo: TikTokLogo },
  spotify:    { name: "Spotify",          color: "#1DB954", Logo: SpotifyLogo },
  facebook:   { name: "Facebook",         color: "#1877F2", Logo: FacebookLogo },
  x:          { name: "X (Twitter)",      color: "#000",    Logo: XLogo },
  linkedin:   { name: "LinkedIn",         color: "#0A66C2", Logo: LinkedInLogo },
  pinterest:  { name: "Pinterest",        color: "#E60023", Logo: PinterestLogo },
  threads:    { name: "Threads",          color: "#000",    Logo: ThreadsLogo },
  snapchat:   { name: "Snapchat",         color: "#FFFC00", Logo: SnapchatLogo },
};
